import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import usePopupClose from '../Utils/usePopupClose';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import Step_Start from './Step_Start';
import Step_SelectCriteria from './Step_SelectCriteria';
import Step_Loading_GeneratingCriteria from './Step_Loading_GeneratingCriteria';
import Step_ConfirmCriteria from './Step_ConfirmCriteria';
import Step_Loading_GeneratingEvaluation from './Step_Loading_GeneratingEvaluation';
import Step_Loading_Completed from './Step_Loading_Completed';

const CreateEvaluationWizard = ({ isOpen, onClose, evaluation_id, jobData = null, talentData = null }) => {
  // Get user session for API authentication and navigation
  const { user, logout } = useSession();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [evaluationData, setEvaluationData] = useState(null);
  const [currentStep, setCurrentStep] = useState('start');
  const [userDefaultSettings, setUserDefaultSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  
  // Initialize allStepData with jobData or talentData if provided
  const [allStepData, setAllStepData] = useState(() => {
    if (jobData && isOpen && !evaluation_id) {
      return {
        evaluationType: 'Job',
        selectedJob: {
          job_id: jobData.id,
          title: jobData.name,
          company: jobData.companyName,
          job_category_json: jobData.job_category_json
        },
        selectedRole: jobData.job_category_json ? {
          subCategory: jobData.job_category_json.subCategory,
          mainCategory: jobData.job_category_json.mainCategory,
          altSubcategory: jobData.job_category_json.altSubcategory
        } : null,
        seniority: 'Senior',
        evaluation_name: `Evaluation ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}, ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}`,
        evaluationSteps: {
          resume: true,
          kyc: false,
          video_intro: false,
          questionnaire: true,
          challenge: true
        }
      };
    } else if (talentData && isOpen && !evaluation_id) {
      console.log("talentData:: ", talentData);
      return {
        evaluationType: 'Talent',
        selectedTalent: {
          talent_id: talentData.id,
          candidate_id: talentData.candidate_id,
          name: `${talentData.fname || ''} ${talentData.lname || ''}`.trim(),
          email: talentData.email,
          roleCategory: talentData.roleCategory
        },
        selectedRole: talentData.roleCategory ? {
          subCategory: talentData.roleCategory.subCategory,
          mainCategory: talentData.roleCategory.mainCategory,
          altSubcategory: talentData.roleCategory.altSubcategory
        } : null,
        seniority: 'Senior',
        evaluation_name: `Evaluation ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}, ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}`,
        evaluationSteps: {
          resume: true,
          kyc: false,
          video_intro: false,
          questionnaire: true,
          challenge: true
        }
      };
    }
    return {};
  });

  // Use the popup close hook to handle ESC key press only (not clicks outside)
  const modalRef = usePopupClose(isOpen, onClose, false, false);
  
  // Ref to access Step_ConfirmCriteria component
  const confirmCriteriaRef = useRef();

  // Fetch user default settings from API
  const fetchUserDefaultSettings = async () => {
    setLoadingSettings(true);
    try {
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Make GET request to load user settings
      const response = await fetch(API_ENDPOINTS.LOAD_USER_DEFAULT_SETTINGS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      
      // Parse the response data
      const data = await response.json();
      console.log('User Default Settings:', data);
      
      // Extract settings
      const settings = data[0]?.evaluation_settings;
      if (!settings) {
        throw new Error('Settings not found in response');
      }
      
      setUserDefaultSettings(data);
      
      // Update allStepData with loaded evaluation steps
      setAllStepData(prev => ({
        ...prev,
        evaluationSteps: settings.evaluation_steps,
        countdown_enabled: settings.countdown_enabled
      }));
      
    } catch (error) {
      console.error('Error fetching user default settings:', error);
      
      // Handle 401 errors by redirecting to login
      if (error.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      // For all other errors, show alert and close wizard
      alert(`Failed to load default settings: ${error.message}`);
      onClose();
    } finally {
      setLoadingSettings(false);
    }
  };

  // Fetch evaluation data from API (following EvaluationDetails pattern)
  const fetchEvaluationData = async (id) => {
    setLoading(true);
    try {
      
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Make GET request to the evaluation details endpoint with evaluation ID as 'eid' parameter
      const url = new URL(API_ENDPOINTS.GET_EVALUATION_DETAILS);
      url.searchParams.append('eid', id);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      if (!response.ok) {
        // Try to parse the error response body to get the server error message
        let errorMessage = `Error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
        
        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }
      
      // Parse the response data
      const data = await response.json();
      console.log('GET_EVALUATION_DETAILS response:', data);
      
      // Transform the data if needed (following EvaluationDetails pattern)
      const transformedData = Array.isArray(data) && data.length > 0 ? data[0] : data;
      
      setEvaluationData(transformedData);
      // Set current step based on evaluation status or default to 'start'
      setCurrentStep(transformedData.status || 'start');
      
    } catch (error) {
      console.error('Error fetching evaluation data:', error);
      
      // Handle 401 errors by redirecting to login (following pattern from other components)
      if (error.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      // On other errors, default to start step
      setCurrentStep('start');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user default settings when modal opens (for new evaluations only)
  useEffect(() => {
    if (isOpen && !evaluation_id) {
      fetchUserDefaultSettings();
    } else if (isOpen) {
      // If editing existing evaluation, no need to load settings
      setLoadingSettings(false);
    }
  }, [isOpen, evaluation_id]);

  // Fetch evaluation data when evaluation_id is provided
  useEffect(() => {
    if (isOpen && evaluation_id) {
      fetchEvaluationData(evaluation_id);
    } else if (isOpen) {
      // No evaluation_id provided, start fresh
      setCurrentStep('start');
      setEvaluationData(null);
      setLoading(false);
    }
  }, [isOpen, evaluation_id]);

  // Handle step data changes - merge data instead of replacing per step
  const handleStepDataChange = (data) => {
    setAllStepData(prev => ({
      ...prev,
      ...data // Merge all data into single object instead of per-step
    }));
  };

  /**
   * Create evaluation via API // THIS COMES FROM DIRECTLY CREATING WIHTOUT CONFIRM CRITERIA
   * @returns 
   */
  const createEvaluation = async () => {
    setButtonLoading(true);
    try {
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }

      // Extract settings - throw error if not loaded
      const settings = userDefaultSettings?.[0]?.evaluation_settings;
      if (!settings) {
        throw new Error('Default evaluation settings not loaded. Please refresh and try again.');
      }

      // Check for required settings
      if (!settings.target_questionnaire_questions || !settings.target_challenge_questions || 
          !settings.target_challenge_time || !settings.target_questionnaire_time ||
          !settings.target_challenge_breadth || !settings.target_questionnaire_breadth ||
          !settings.challenge_type || !settings.evaluation_steps) {
        throw new Error('Incomplete evaluation settings. Please configure settings in Settings page.');
      }

      // Create simplified payload with minimal transformation
      const payload = {
        type: allStepData.type || 'VSSkillset',
        talent_id: allStepData.selectedTalent?.talent_id || null,
        candidate_id: allStepData.selectedTalent?.candidate_id || null,
        job_id: allStepData.selectedJob?.job_id || null,
        evaluation_name: allStepData.evaluation_name || '',
        role: allStepData.selectedRole || null,
        seniority: allStepData.seniority || 'Senior',
        is_default_evaluation: allStepData.is_default_evaluation || false,
        generate_directly: allStepData.generate_directly || false,
        evaluation_config: {
          target_questionnaire_questions: String(settings.target_questionnaire_questions),
          target_challenge_questions: String(settings.target_challenge_questions),
          target_challenge_time: String(settings.target_challenge_time),
          target_questionnaire_time: String(settings.target_questionnaire_time),
          target_challenge_breadth: String(settings.target_challenge_breadth),
          target_questionnaire_breadth: String(settings.target_questionnaire_breadth),
          challenge_type: String(settings.challenge_type),
          countdown_enabled: settings.countdown_enabled,
          evaluation_steps: allStepData.evaluationSteps || settings.evaluation_steps
        },
        vsskillset_user_criteria_input: allStepData.vsskillset_user_criteria_input || '',
        extra_criteria_instructions: allStepData.extra_criteria_instructions || ''
      };

      console.log('Creating evaluation with payload:', payload);
      
      // Make POST request to create evaluation
      const response = await fetch(API_ENDPOINTS.V1_CREATE_EVALUATION, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        // Try to parse the error response body to get the server error message
        let errorMessage = `Error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
        
        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      // Parse the response data
      const data = await response.json();
      console.log('V1_CREATE_EVALUATION response:', data);

      // Transform the data the same way as GET_EVALUATION_DETAILS (array to single object)
      const transformedData = Array.isArray(data) && data.length > 0 ? data[0] : data;
      
      // Store the evaluation data from the server response
      setEvaluationData(transformedData);

      // Navigate to generating criteria step
      setCurrentStep('evs_generating_criteria');
      
    } catch (error) {
      console.error('Error creating evaluation:', error);
      
      // Handle 401 errors by redirecting to login
      if (error.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      // For other errors, show an alert (you might want to implement better error handling)
      alert(`Error creating evaluation: ${error.message}`);
    } finally {
      setButtonLoading(false);
    }
  };

  /**
   * Generate evaluation with updated criteria via API // THIS COMES FROM THE CONFIRMCRITERIA
   * @param {*} questionnaireJson 
   * @param {*} challengeJson 
   * @param {*} targetQuestionnaireQuestions 
   * @param {*} targetChallengeQuestions 
   * @param {*} targetQuestionnaireTime 
   * @param {*} targetChallengeTime 
   * @param {*} challengeBreadth 
   * @param {*} challengeType 
   * @returns 
   */
  const generateEvaluation = async (questionnaireJson, challengeJson, targetQuestionnaireQuestions, targetChallengeQuestions, targetQuestionnaireTime, targetChallengeTime, challengeBreadth, challengeType) => {
    
    setButtonLoading(true);
    try {
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }

      // Create payload with updated criteria, target question counts, target times, breadth, and challenge type
      const payload = {
        evaluation_id: evaluationData?.evaluation_id,
        confirmed_questionnaire_criteria_json: questionnaireJson,
        confirmed_challenge_criteria_json: challengeJson,
        target_questionnaire_questions: String(targetQuestionnaireQuestions || 10),
        target_challenge_questions: String(targetChallengeQuestions || 10),
        target_questionnaire_time: String(targetQuestionnaireTime || 30),
        target_challenge_time: String(targetChallengeTime || 60),
        challenge_breadth: challengeBreadth || 'Mixed',
        challenge_type: challengeType || 'quiz'
      };

      console.log('Generating evaluation with updated criteria:', payload);
      
      // Make POST request to generate evaluation with updated criteria
      const response = await fetch(API_ENDPOINTS.V1_CREATE_QC, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        // Try to parse the error response body to get the server error message
        let errorMessage = `Error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
        
        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      const data = await response.json();

      // Transform the data the same way as other API responses (array to single object)
      const transformedData = Array.isArray(data) && data.length > 0 ? data[0] : data;
      
      // Update evaluation data with the response
      setEvaluationData(transformedData);

      console.log('V1_CREATE_QC response transformedData:', transformedData);
      
      // Update the current step based on the new status from server response
      setCurrentStep(transformedData.status || 'evs_generating_questionnaire');
      
    } catch (error) {
      console.error('Error generating evaluation:', error);
      
      // Handle 401 errors by redirecting to login
      if (error.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      // For other errors, show an alert
      alert(`Error generating evaluation: ${error.message}`);
    } finally {
      setButtonLoading(false);
    }
  };


  // Handle next button click
  const handleNext = () => {
    console.log('Next clicked with data:', allStepData);
    
    if (currentStep === 'start') {
      // Validate the start step before proceeding
      if (allStepData.validateForm && !allStepData.validateForm()) {
        return; // Validation failed, errors are shown inline
      }
      setCurrentStep('selectCriteria');
    } else if (currentStep === 'selectCriteria') {
      // Create evaluation via API
      createEvaluation();
    }
  };

  // Handle previous button click
  const handlePrevious = () => {
    if (currentStep === 'selectCriteria') {
      setCurrentStep('start');
    }
  };

  // Handle refresh for generating criteria step
  const handleRefresh = () => {
    // Use evaluation_id prop if available, otherwise use evaluation_id from stored evaluation data
    const idToUse = evaluation_id || evaluationData?.evaluation_id;
    if (idToUse) {
      fetchEvaluationData(idToUse);
    }
  };

  // Define which steps should show navigation buttons
  const shouldShowNavigation = () => {
    const stepsWithNavigation = ['start', 'selectCriteria', 'evs_criteria_review'];
    return stepsWithNavigation.includes(currentStep);
  };

  // Get current step component
  const getCurrentStepComponent = () => {
    switch (currentStep) {
      case 'start':
        return (
          <Step_Start
            data={allStepData}
            onDataChange={handleStepDataChange}
            evaluationData={evaluationData}
          />
        );
      case 'selectCriteria':
        return (
          <Step_SelectCriteria
            data={allStepData}
            onDataChange={handleStepDataChange}
            evaluationData={evaluationData}
          />
        );
      case 'evs_generating_criteria': // Actual server status for generating criteria
        return (
          <Step_Loading_GeneratingCriteria
            data={allStepData}
            onDataChange={handleStepDataChange}
            evaluationData={evaluationData}
            onRefresh={handleRefresh}
            onClose={onClose}
          />
        );
      case 'evs_criteria_review': // Actual server status for criteria review
        return (
          <Step_ConfirmCriteria
            ref={confirmCriteriaRef}
            data={allStepData}
            onDataChange={handleStepDataChange}
            evaluationData={evaluationData}
            onClose={onClose}
            onGenerateEvaluation={generateEvaluation}
          />
        );
      case 'evs_generating_questionnaire': // Server status for generating questionnaire
      case 'evs_generating_challenge': // Server status for generating challenge
        return (
          <Step_Loading_GeneratingEvaluation
            data={allStepData}
            onDataChange={handleStepDataChange}
            evaluationData={evaluationData}
            onRefresh={handleRefresh}
            onClose={onClose}
          />
        );
      case 'evs_available': // Server status for completed evaluation
        return (
          <Step_Loading_Completed
            data={allStepData}
            onDataChange={handleStepDataChange}
            evaluationData={evaluationData}
            onClose={onClose}
          />
        );
      default:
        return (
          <Step_Start
            data={allStepData}
            onDataChange={handleStepDataChange}
            evaluationData={evaluationData}
          />
        );
    }
  };

  // If modal is not open, don't render anything
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] overflow-visible">
      {/* Backdrop - dark blur backdrop */}
      <div className="fixed inset-0 bg-gray-900 backdrop-blur-sm bg-opacity-30"></div>
      
      {/* Modal - Made larger for two-column layout */}
      <div ref={modalRef} className="relative bg-white rounded-lg shadow-xl max-w-5xl w-full h-[90vh] flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }}>
        {/* Close button - Top right */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Modal content - No header */}
        <div className="flex-1 overflow-hidden">
        {(loading || loadingSettings) ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          </div>
        ) : (
            <div className="h-full overflow-hidden">
              {getCurrentStepComponent()}
            </div>
          )}
        </div>
        
        {/* Navigation buttons - Fixed at bottom, only show for steps that need them */}
        {!loading && shouldShowNavigation() && (
          <div className="absolute bottom-0 left-0 right-0 px-8 py-6 border-t border-gray-200 flex justify-between rounded-b-lg shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]" style={{ backgroundColor: 'var(--color-brand-blue-light)' }}>
            {(currentStep === 'start' || currentStep === 'evs_criteria_review') ? (
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Close
              </button>
            ) : (
              <button
                onClick={handlePrevious}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Back
              </button>
            )}
            
            {currentStep !== 'evs_criteria_review' && (
              <button
                onClick={handleNext}
                disabled={buttonLoading}
                className={`px-6 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 ${
                  buttonLoading 
                    ? 'bg-blue-400 text-white cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {buttonLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                {currentStep === 'selectCriteria' ? 'Next' : 'Next'}
              </button>
            )}
            
            {currentStep === 'evs_criteria_review' && (
              <button
                onClick={() => {
                  // Call the handleGenerateEvaluation function from Step_ConfirmCriteria via ref
                  if (confirmCriteriaRef.current) {
                    confirmCriteriaRef.current.handleGenerateEvaluation();
                  }
                }}
                disabled={buttonLoading}
                className={`px-6 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 ${
                  buttonLoading 
                    ? 'bg-blue-400 text-white cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {buttonLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                Generate Evaluation
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateEvaluationWizard;
